import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Ban, CheckCircle2, Circle, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { API, MODE_LABELS, ROLE_NAMES, errorText } from './constants';

const EDITABLE = [['amount', 'Monto', 'number'], ['impact_pct', 'Impacto %', 'number'], ['risk_level', 'Riesgo', 'risk'],
  ['category', 'Categoría'], ['module_type', 'Módulo'], ['confidence', 'Confianza', 'number']];
const RISKS = { '': 'Riesgo: sin definir', low: 'Bajo', medium: 'Medio', high: 'Alto', critical: 'Crítico' };

const useRefresh = (companyId, proposalId) => {
  const qc = useQueryClient();
  return () => { qc.invalidateQueries({ queryKey: ['v2-proposals', companyId] }); qc.invalidateQueries({ queryKey: ['v2-proposal', proposalId] }); };
};

const STATUS_TOAST = { pending: 'Corregida: ahora espera aprobación', auto_cleared: 'Corregida: ya no requiere aprobación', blocked: 'Sigue bloqueada por una regla' };

const CorrectForm = ({ companyId, p }) => {
  const refresh = useRefresh(companyId, p.id);
  const [f, setF] = useState(Object.fromEntries(EDITABLE.map(([k]) => [k, p[k] ?? ''])));
  const save = useMutation({
    mutationFn: async () => (await axios.put(`${API}/companies/${companyId}/proposals/${p.id}`, {
      title: p.title, description: p.description || '', agent_name: p.agent_name, iad_code: p.iad_code, kpi_name: p.kpi_name,
      expected_value: p.expected_value, expected_date: p.expected_date,
      ...Object.fromEntries(EDITABLE.map(([k, , t]) => [k, f[k] === '' ? null : t === 'number' ? Number(f[k]) : f[k]])) })).data,
    onSuccess: (d) => { toast[d.proposal.status === 'blocked' ? 'warning' : 'success'](STATUS_TOAST[d.proposal.status] || 'Corregida'); refresh(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <div className="space-y-2" data-testid="proposal-correct-form">
      <p className="text-xs font-medium text-slate-500">Corregir los datos de la propuesta (se vuelven a evaluar las reglas)</p>
      <div className="grid grid-cols-3 gap-2">
        {EDITABLE.map(([k, label, t]) => (t === 'risk'
          ? <select key={k} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="h-9 rounded-md border border-slate-200 bg-transparent px-2 text-sm dark:border-slate-700" data-testid={`proposal-correct-${k}`}>
            {Object.entries(RISKS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
          : <Input key={k} type={t || 'text'} placeholder={label} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`proposal-correct-${k}`} />))}
      </div>
      <Button size="sm" onClick={() => save.mutate()} disabled={save.isPending} data-testid="proposal-correct-submit">{save.isPending ? 'Evaluando…' : 'Guardar corrección'}</Button>
    </div>
  );
};

const ExceptionForm = ({ companyId, p, ruleId }) => {
  const refresh = useRefresh(companyId, p.id);
  const [text, setText] = useState('');
  const grant = useMutation({
    mutationFn: () => axios.post(`${API}/companies/${companyId}/proposals/${p.id}/exception`, { rule_id: ruleId, justification: text }),
    onSuccess: () => { toast.success('Excepción autorizada y registrada'); refresh(); },
    onError: (e) => toast.error(errorText(e)),
  });
  return (
    <div className="space-y-2" data-testid={`proposal-exception-${ruleId}`}>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="Justificación de la excepción (mínimo 10 caracteres)" data-testid={`proposal-exception-text-${ruleId}`} />
      <Button size="sm" variant="outline" className="border-red-400 text-red-700" disabled={text.trim().length < 10 || grant.isPending} onClick={() => grant.mutate()} data-testid={`proposal-exception-submit-${ruleId}`}>
        <ShieldCheck className="mr-1 h-4 w-4" />Autorizar excepción</Button>
    </div>
  );
};

export const BlockedBox = ({ companyId, p, perms }) => (
  <div className="space-y-3 rounded-lg border border-red-300 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/30" data-testid="proposal-blocked-box">
    <p className="flex items-center gap-2 font-semibold text-red-700 dark:text-red-400"><Ban className="h-4 w-4" />Bloqueada por política institucional</p>
    {p.blocked_by.map((b) => (
      <div key={b.rule_id} className="space-y-2 text-sm" data-testid={`proposal-blocked-rule-${b.rule_id}`}>
        <p>• <b>{b.name}</b> (v{b.version}) · {b.allow_exception ? `admite excepción de ${ROLE_NAMES[b.exception_role]}` : 'no admite excepciones'}</p>
        {perms?.can_grant_exception_rules?.includes(b.rule_id) && <ExceptionForm companyId={companyId} p={p} ruleId={b.rule_id} />}
      </div>))}
    <p className="text-xs text-slate-600 dark:text-slate-300">Una aprobación no la desbloquea. Corrige los datos o pide la excepción a quien la política autoriza.</p>
    {perms?.can_correct && <CorrectForm companyId={companyId} p={p} />}
  </div>
);

export const WarningsBox = ({ warnings }) => (!warnings?.length ? null : (
  <div className="rounded-lg border border-orange-300 bg-orange-50 p-3 text-sm dark:border-orange-900 dark:bg-orange-950/30" data-testid="proposal-warnings">
    <p className="mb-1 flex items-center gap-2 font-semibold text-orange-700 dark:text-orange-400"><AlertTriangle className="h-4 w-4" />Advertencias</p>
    {warnings.map((w) => <p key={w.rule_id}>• {w.name} (v{w.version}, {w.severity})</p>)}
  </div>
));

const reqDetail = (r) => {
  if (r.mode === 'sequential') return r.levels.map((l, i) => `N${i + 1} ${r.level_counts?.[i] ?? 0}/${l.min_approvals} ${ROLE_NAMES[l.role]}${r.current_level === i ? ' ← actual' : ''}`).join(' · ');
  if (r.mode === 'all_named') return `${r.approved}/${r.required} personas elegidas`;
  return `${r.approved}/${r.required} de ${ROLE_NAMES[r.approver_role]}`;
};

export const ApprovalProgress = ({ progress, approvals }) => (!progress?.length ? null : (
  <div className="space-y-2 rounded-lg border border-slate-200 p-3 dark:border-slate-700" data-testid="proposal-approval-progress">
    <p className="text-xs font-medium text-slate-500">Flujo de aprobación</p>
    {progress.map((r, i) => (
      <p key={r.rule_id || i} className="flex items-center gap-2 text-sm" data-testid={`proposal-requirement-${i}`}>
        {r.satisfied ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-400" />}
        <b>{r.name}</b><span className="text-xs text-slate-500">{MODE_LABELS[r.mode]} · {reqDetail(r)}</span>
      </p>))}
    {(approvals || []).map((a) => <p key={a.user_id} className="text-xs text-slate-500">✓ {a.email} ({ROLE_NAMES[a.role]}): “{a.comment}”</p>)}
  </div>
));
